import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/db/client";
import { useEffect } from "react";
import { toast } from "sonner";

export interface Subsheet {
  id: string;
  name: string;
  description: string | null;
  created_by_id: string;
  created_at: string;
  order_index: number;
  /** The page this one sits inside, or null for top-level. Access flows down it. */
  parent_id: string | null;
  /** Where new tickets here start (#9304); null inherits the nearest parent's. */
  default_pipeline_id: string | null;
}

export const useSubsheets = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel("subsheets-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "subsheets",
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["subsheets"] });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return useQuery<Subsheet[]>({
    queryKey: ["subsheets"],
    queryFn: async () => {
      const { data, error } = await supabase.from("subsheets").select("*").order("order_index", { ascending: true });

      if (error) throw error;
      return data || [];
    },
    staleTime: Infinity,
    gcTime: 24 * 60 * 60 * 1000,
  });
};

export const useCreateSubsheet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      name,
      description,
      parentId,
    }: {
      name: string;
      description?: string;
      parentId?: string | null;
    }) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Get the max order_index to append at the end
      const { data: maxOrderData } = await supabase
        .from("subsheets")
        .select("order_index")
        .order("order_index", { ascending: false })
        .limit(1)
        .single();

      const newOrderIndex = (maxOrderData?.order_index ?? -1) + 1;

      const { data, error } = await supabase
        .from("subsheets")
        .insert({
          name,
          description: description || null,
          created_by_id: user.id,
          order_index: newOrderIndex,
          parent_id: parentId ?? null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subsheets"] });
    },
  });
};

export const useUpdateSubsheet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      name,
      description,
      parentId,
    }: {
      id: string;
      name: string;
      description?: string;
      // undefined leaves the parent alone; null moves the page to top level.
      parentId?: string | null;
    }) => {
      const { data, error } = await supabase
        .from("subsheets")
        .update({
          name,
          description: description || null,
          ...(parentId === undefined ? {} : { parent_id: parentId }),
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subsheets"] });
    },
  });
};

export const useReorderSubsheets = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      // Update each subsheet's order_index based on its position in the array
      const updates = orderedIds.map((id, index) =>
        supabase.from("subsheets").update({ order_index: index }).eq("id", id),
      );

      const results = await Promise.all(updates);
      const errors = results.filter((r) => r.error);
      if (errors.length > 0) {
        throw new Error("Failed to reorder subsheets");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subsheets"] });
    },
  });
};

// Swaps two SIBLING departments' order_index to move one up or down among the
// pages sharing its parent. A cross-branch swap is meaningless in a tree, so
// the caller (the settings page) only ever passes adjacent siblings.
export const useSwapSubsheetOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ([a, b]: [Subsheet, Subsheet]) => {
      const results = await Promise.all([
        supabase.from("subsheets").update({ order_index: b.order_index }).eq("id", a.id),
        supabase.from("subsheets").update({ order_index: a.order_index }).eq("id", b.id),
      ]);
      const failed = results.find((r) => r.error);
      if (failed?.error) throw failed.error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subsheets"] });
    },
    onError: (e: Error) => toast.error(e.message || "Could not reorder"),
  });
};

// Goes through the delete_subsheet RPC rather than a plain delete: subsheet_ids
// is an array with no element-level FK, so nothing else stops a ticket from
// being left holding a dangling id. The RPC reassigns tickets for which the
// doomed department was their only one, per migration 0045.
export const useDeleteSubsheet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, replacementId }: { id: string; replacementId: string | null }) => {
      const { error } = await supabase.rpc("delete_subsheet", {
        _subsheet_id: id,
        _replacement_id: replacementId,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subsheets"] });
      // The RPC may have rewritten subsheet_ids on affected tickets.
      queryClient.invalidateQueries({ queryKey: ["backlog-items"] });
      queryClient.invalidateQueries({ queryKey: ["backlog-item"] });
      toast.success("Department deleted");
    },
    onError: (e: Error) => toast.error(e.message || "Could not delete the department"),
  });
};

/** How many tickets sit in a department, and how many have it as their ONLY
 *  department (those need a replacement before deletion, per delete_subsheet).
 *  Counts every ticket, deleted or not, matching what the RPC touches. */
export async function countTicketsOnSubsheet(subsheetId: string): Promise<{ attached: number; orphaned: number }> {
  const { data, error } = await supabase
    .from("backlog_items")
    .select("subsheet_ids")
    .contains("subsheet_ids", [subsheetId]);
  if (error) throw error;
  const rows = data || [];
  const orphaned = rows.filter((r) => (r.subsheet_ids ?? []).filter((id) => id !== subsheetId).length === 0).length;
  return { attached: rows.length, orphaned };
}
